/**
 * Full-screen detail view for a single interaction.
 *
 * Shared between the Me tab (Recent Interactions feed) and the
 * Contact detail Interactions tab. Both entry points open this same
 * surface so the behavior is identical — tap from anywhere, see
 * the same card.
 *
 * Layout (top → bottom):
 *   1. Meta row — relative time, duration, location label chip
 *   2. Summary — one-sentence derived summary (from Phase 3p facts
 *      when available, falling back to transcript preview)
 *   3. "What mattered" — Phase 3p structured facts, rendered as
 *      named sections (highlights, commitments, open questions,
 *      notable quotes, people mentioned, topics, sentiment). Absent
 *      array fields don't render their section — a facts-poor
 *      conversation gets a cleanly compact view.
 *   4. Transcript — expandable section powered by ProcessedTurn rows
 *      fetched on demand. Visible to all users during active
 *      development so we can verify ASR accuracy on real recordings;
 *      will be gated behind Dev Mode before public launch.
 *   5. Location / map pin — unchanged from Phase 3m.
 *
 * When a session was recorded before location permission was granted
 * (or the fix timed out), we surface an explicit empty state so the
 * user understands the pin is absent for a reason, not because the
 * feature is broken.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Clock,
  MapPin,
  MapPinOff,
  Navigation,
  Quote,
  X,
} from "lucide-react";

import type { Contact } from "@/lib/data";
import type {
  ConversationFacts,
  ProcessedInteraction,
  ProcessedTurn,
} from "@/types/voice";

import {
  displayNameForContactId,
  formatInteractionDuration,
  formatRelativeTime,
} from "@/utils/interactionDisplay/interactionDisplay";
import { listTurnsForSession } from "@/hooks/useSessionProcessor";

import { LocationMap } from "./LocationMap";

interface InteractionDetailProps {
  interaction: ProcessedInteraction;
  contacts: Contact[];
  onClose: () => void;
}

export function InteractionDetail({
  interaction,
  contacts,
  onClose,
}: InteractionDetailProps) {
  const contactName = displayNameForContactId(interaction.contactId, contacts);
  const summary =
    interaction.facts?.summary?.trim() ||
    interaction.summary?.trim() ||
    interaction.transcriptPreview?.trim() ||
    "(No summary yet — check back after processing finishes.)";
  const hasLocation =
    interaction.latitude !== null &&
    interaction.longitude !== null &&
    Number.isFinite(interaction.latitude) &&
    Number.isFinite(interaction.longitude);

  return (
    <div className="fixed inset-0 z-[9500] flex flex-col bg-background text-foreground">
      <header
        className="flex items-center justify-between border-b border-[rgba(255,255,255,0.06)] px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <div className="flex min-w-0 flex-col">
          <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
            Interaction
          </p>
          <h2 className="text-foreground truncate text-[18px] font-semibold">
            {contactName}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full border border-[rgba(255,255,255,0.1)] p-2 active:scale-95"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div
        className="flex-1 overflow-y-auto px-5 py-6"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        }}
      >
        <div className="flex flex-col gap-5">
          <section className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[rgba(255,255,255,0.6)]">
            <MetaChip
              icon={<Clock className="h-3.5 w-3.5" />}
              label={formatRelativeTime(interaction.createdAt)}
            />
            <MetaChip
              icon={<Navigation className="h-3.5 w-3.5" />}
              label={formatInteractionDuration(interaction.totalSecondsSpoken)}
            />
            {interaction.locationLabel && (
              <MetaChip
                icon={<MapPin className="h-3.5 w-3.5" />}
                label={interaction.locationLabel}
              />
            )}
          </section>

          <section>
            <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
              Summary
            </p>
            <p className="text-foreground mt-2 text-[16px] leading-relaxed">
              {summary}
            </p>
          </section>

          {interaction.facts && <FactsBlock facts={interaction.facts} />}

          <TranscriptBlock
            sessionId={interaction.sessionId}
            contacts={contacts}
            contactName={contactName}
          />

          {hasLocation ? (
            <LocationSection
              latitude={interaction.latitude as number}
              longitude={interaction.longitude as number}
              label={interaction.locationLabel}
            />
          ) : (
            <NoLocationSection />
          )}
        </div>
      </div>
    </div>
  );
}

function MetaChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {icon}
      <span>{label}</span>
    </span>
  );
}

// ── Phase 3p — "What mattered" structured facts ──

interface FactsBlockProps {
  facts: ConversationFacts;
}

function FactsBlock({ facts }: FactsBlockProps) {
  const hasAnything =
    facts.highlights.length > 0 ||
    facts.commitments.length > 0 ||
    facts.openQuestions.length > 0 ||
    facts.notableQuotes.length > 0 ||
    facts.peopleMentioned.length > 0 ||
    facts.topics.length > 0;
  if (!hasAnything) return null;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-[rgba(201,168,76,0.12)] bg-[rgba(201,168,76,0.03)] p-4">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(201,168,76,0.7)]">
        What mattered
      </p>

      {facts.highlights.length > 0 && (
        <FactList items={facts.highlights} />
      )}

      {facts.commitments.length > 0 && (
        <FactGroup label="Commitments" items={facts.commitments} />
      )}

      {facts.openQuestions.length > 0 && (
        <FactGroup label="Left open" items={facts.openQuestions} />
      )}

      {facts.notableQuotes.length > 0 && (
        <QuotesGroup quotes={facts.notableQuotes} />
      )}

      {(facts.peopleMentioned.length > 0 || facts.topics.length > 0) && (
        <div className="flex flex-col gap-2">
          {facts.peopleMentioned.length > 0 && (
            <ChipRow label="People" values={facts.peopleMentioned} />
          )}
          {facts.topics.length > 0 && (
            <ChipRow label="Topics" values={facts.topics} />
          )}
        </div>
      )}

      {facts.sentiment && (
        <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.35)]">
          Tone — <span className="text-[rgba(255,255,255,0.6)]">{facts.sentiment}</span>
        </p>
      )}
    </section>
  );
}

function FactList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5 text-[14px] leading-relaxed text-[rgba(255,255,255,0.85)]">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-2">
          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[rgba(201,168,76,0.7)]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function FactGroup({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        {label}
      </p>
      <ul className="flex flex-col gap-1 text-[14px] leading-relaxed text-[rgba(255,255,255,0.8)]">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function QuotesGroup({ quotes }: { quotes: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        Worth remembering
      </p>
      <ul className="flex flex-col gap-2">
        {quotes.map((quote, idx) => (
          <li
            key={idx}
            className="flex gap-2 border-l-2 border-[rgba(201,168,76,0.4)] pl-3 text-[14px] italic leading-relaxed text-[rgba(255,255,255,0.75)]"
          >
            <Quote className="mt-0.5 h-3 w-3 shrink-0 text-[rgba(201,168,76,0.5)]" />
            <span>{quote}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChipRow({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
        {label}
      </span>
      {values.map((value, idx) => (
        <span
          key={idx}
          className="rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-2.5 py-0.5 text-[12px] text-[rgba(255,255,255,0.75)]"
        >
          {value}
        </span>
      ))}
    </div>
  );
}

// ── Phase 3o — expandable transcript view ──

interface TranscriptBlockProps {
  sessionId: string;
  contacts: Contact[];
  contactName: string;
}

function TranscriptBlock({
  sessionId,
  contacts,
  contactName,
}: TranscriptBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const [turns, setTurns] = useState<ProcessedTurn[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTurns = useCallback(async () => {
    if (turns !== null) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await listTurnsForSession(sessionId);
      fetched.sort((a, b) => a.startSeconds - b.startSeconds);
      setTurns(fetched);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId, turns]);

  useEffect(() => {
    if (expanded) void loadTurns();
  }, [expanded, loadTurns]);

  const labelForSpeaker = useMemo(() => {
    return (contactId: string | null): string => {
      if (contactId === null || contactId === undefined) return "Unknown";
      return displayNameForContactId(contactId, contacts);
    };
  }, [contacts]);

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] px-4 py-3 active:bg-[rgba(255,255,255,0.04)]"
      >
        <span className="text-[13px] font-medium uppercase tracking-[0.15em] text-[rgba(255,255,255,0.55)]">
          Transcript{turns ? ` · ${turns.length} turns` : ""}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[rgba(255,255,255,0.4)] transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="mt-3 flex flex-col gap-3">
          {loading && (
            <p className="text-[13px] text-[rgba(255,255,255,0.4)]">
              Loading transcript…
            </p>
          )}
          {error && (
            <p className="text-[13px] text-[rgba(255,120,120,0.9)]">
              Couldn't load transcript: {error}
            </p>
          )}
          {turns && turns.length === 0 && (
            <p className="text-[13px] text-[rgba(255,255,255,0.4)]">
              No transcript available for this session.
            </p>
          )}
          {turns && turns.length > 0 && (
            <ol className="flex flex-col gap-3">
              {turns.map((turn) => (
                <TranscriptTurn
                  key={turn.id}
                  turn={turn}
                  speakerName={labelForSpeaker(turn.contactId)}
                  contactName={contactName}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

interface TranscriptTurnProps {
  turn: ProcessedTurn;
  speakerName: string;
  contactName: string;
}

function TranscriptTurn({
  turn,
  speakerName,
  contactName,
}: TranscriptTurnProps) {
  const text = turn.transcript?.trim();
  const isUser = turn.contactId === "user_self";
  const isContact = !isUser && turn.contactId !== null;
  // Phase 3u — unknown-cluster turns carry a stable "Speaker A/B/..."
  // label from the cross-session catalog, so multiple unknown voices
  // in one transcript remain distinguishable and recurring voices keep
  // the same label across sessions.
  const displayLabel = isUser
    ? "You"
    : isContact
      ? speakerName
      : (turn.speakerLabel ?? "Unknown voice");

  // Speakers get distinct accent colors so the transcript reads like
  // a conversation rather than a flat wall of text. Amber for the
  // contact, neutral-white for the user, muted for unresolved turns.
  const labelClass = isUser
    ? "text-[rgba(255,255,255,0.7)]"
    : isContact
      ? "text-[rgba(201,168,76,0.85)]"
      : "text-[rgba(255,255,255,0.35)]";

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className={`text-[12px] font-medium ${labelClass}`}>
          {displayLabel}
        </span>
        <span className="font-mono text-[10px] text-[rgba(255,255,255,0.3)]">
          {formatTimestamp(turn.startSeconds)}
        </span>
      </div>
      <p className="text-[14px] leading-relaxed text-[rgba(255,255,255,0.85)]">
        {text || <span className="italic text-[rgba(255,255,255,0.3)]">(no transcript for this turn)</span>}
      </p>
    </li>
  );
  // contactName is referenced by callers for context but not directly
  // inside this turn row; keep it in the prop interface for future
  // per-turn labeling (e.g. showing "Dad" vs a generic contact name).
  void contactName;
}

function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

// ── Location (unchanged from Phase 3m) ──

interface LocationSectionProps {
  latitude: number;
  longitude: number;
  label: string | null;
}

function LocationSection({ latitude, longitude, label }: LocationSectionProps) {
  const appleMapsUrl = `https://maps.apple.com/?ll=${latitude.toFixed(
    6
  )},${longitude.toFixed(6)}&q=${encodeURIComponent(label ?? "Interaction")}`;

  return (
    <section>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
        Where
      </p>
      <div className="mt-2 overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.08)]">
        <LocationMap latitude={latitude} longitude={longitude} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[12px]">
        <span className="text-muted-foreground font-mono">
          {latitude.toFixed(5)}, {longitude.toFixed(5)}
        </span>
        <a
          href={appleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-[rgba(255,255,255,0.1)] px-3 py-1 text-white/70 active:scale-95"
        >
          Open in Maps
        </a>
      </div>
    </section>
  );
}

/**
 * Explicit empty state when the interaction has no location. This
 * happens for sessions recorded before location permission was
 * granted, or when the GPS fix timed out. Without this, a missing
 * pin looks like a bug; with this, it reads as "this one just
 * doesn't have a location."
 */
function NoLocationSection() {
  return (
    <section>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.2em]">
        Where
      </p>
      <div className="mt-2 flex items-start gap-3 rounded-2xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-4">
        <MapPinOff className="text-ink-4 mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex flex-col gap-1">
          <p className="text-[14px] text-[rgba(255,255,255,0.7)]">
            No location captured for this conversation.
          </p>
          <p className="text-ink-4 text-[12px] leading-relaxed">
            Sessions recorded before you granted location permission
            don&rsquo;t have a pin. Future conversations will.
          </p>
        </div>
      </div>
    </section>
  );
}
